import * as React from "react"
import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import clsx from 'clsx'
import { useAuth } from '@/features/auth/AuthContext'
import { homePathForRole, canAccessPath } from '@/features/auth/types'
import { forgotPassword } from '@/features/auth/api'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import './LoginPage.css'

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

type FormValues = z.infer<typeof schema>

/** eCare | LTC lite wordmark. Uses the white logo on the navy hero and the
 *  navy logo on the white form side. */
function Brand({ variant, className }: { variant: 'light' | 'dark'; className?: string }): React.JSX.Element {
  return (
    <img
      src={variant === 'light' ? '/vitals-logo.svg' : '/vitals-logo-dark.svg'}
      alt="eCareLTC Vitals"
      className={`login-brand-logo${className ? ` ${className}` : ''}`}
    />
  )
}

const VITAL_ICONS: Record<string, React.JSX.Element> = {
  bp: <path d="M3 12h3.5l2-5 3 10 2.5-7H21" />,
  pulse: <path d="M20.8 6.6a4.6 4.6 0 0 0-8-3 4.6 4.6 0 0 0-8 3c0 5 8 10 8 10s8-5 8-10z" />,
  temp: <path d="M14 14V5a2 2 0 1 0-4 0v9a4 4 0 1 0 4 0z" />,
  glucose: <path d="M12 3s5.5 6.4 5.5 10.5a5.5 5.5 0 0 1-11 0C6.5 9.4 12 3 12 3z" />,
  weight: <path d="M5 8h14M5 12h14M5 16h9" />,
}

const VITALS: Array<{ key: string; label: string; value: string; unit: string; tone: 'blue' | 'pink' }> = [
  { key: 'bp', label: 'Blood pressure', value: '128 / 82', unit: 'mmHg', tone: 'blue' },
  { key: 'pulse', label: 'Pulse', value: '72', unit: 'bpm', tone: 'blue' },
  { key: 'temp', label: 'Temperature', value: '98.4', unit: '°F', tone: 'blue' },
  { key: 'glucose', label: 'Glucose', value: '142', unit: 'mg/dL · high', tone: 'pink' },
  { key: 'weight', label: 'Weight', value: '142', unit: 'lb', tone: 'blue' },
]

const FAX_ROWS: Array<{ label: string; value: string; high?: boolean }> = [
  { label: 'Blood pressure', value: '128 / 82 mmHg' },
  { label: 'Pulse', value: '72 bpm' },
  { label: 'Temperature', value: '98.4 °F' },
  { label: 'Glucose', value: '142 mg/dL · HIGH', high: true },
  { label: 'Weight', value: '142 lb' },
]

// Hero reveal timeline: pills appear one-by-one, the fax slides in, its rows
// fill, then the Delivered badge pops. Mirrors index.html's call-scene cadence.
const REVEAL_SEQUENCE: Array<[number, string]> = [
  [200, 'pill-0'],
  [560, 'pill-1'],
  [920, 'pill-2'],
  [1280, 'pill-3'],
  [1640, 'pill-4'],
  [2150, 'fax'],
  [2450, 'row-0'],
  [2700, 'row-1'],
  [2950, 'row-2'],
  [3200, 'row-3'],
  [3450, 'row-4'],
  [4000, 'badge'],
]
const LOOP_MS = 7200
const ALL_KEYS = [
  ...VITALS.map((_, i) => `pill-${i}`),
  'fax',
  ...FAX_ROWS.map((_, i) => `row-${i}`),
  'badge',
]

/** Drives the looping hero reveal; returns the set of revealed element keys.
 *  Honors prefers-reduced-motion by revealing everything at once. */
function useHeroReveal(): Set<string> {
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    if (reduce) {
      setRevealed(new Set(ALL_KEYS))
      return
    }

    let timers: number[] = []
    const play = (): void => {
      timers.forEach((t) => window.clearTimeout(t))
      setRevealed(new Set())
      timers = REVEAL_SEQUENCE.map(([ms, key]) =>
        window.setTimeout(() => {
          setRevealed((prev) => {
            const next = new Set(prev)
            next.add(key)
            return next
          })
        }, ms),
      )
    }

    play()
    const loop = window.setInterval(play, LOOP_MS)
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      window.clearInterval(loop)
    }
  }, [])

  return revealed
}

export function LoginPage(): React.JSX.Element {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [submitting, setSubmitting] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const revealed = useHeroReveal()

  const [forgotSending, setForgotSending] = useState(false)
  const [forgotSentTo, setForgotSentTo] = useState<string | null>(null)
  const [forgotEmailError, setForgotEmailError] = useState('')
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  async function onForgot(e: React.MouseEvent): Promise<void> {
    e.preventDefault()
    const email = getValues('email').trim()
    if (!email) {
      setForgotEmailError('Enter your email address above first.')
      return
    }
    setForgotEmailError('')
    setForgotSending(true)
    try {
      const res = await forgotPassword(email)
      if (res.reset_url) setDevResetUrl(res.reset_url)
    } catch {
      // Always show the success screen — never reveal rate-limits or
      // whether the email is registered.
    } finally {
      setForgotSending(false)
      setForgotSentTo(email)
    }
  }

  function onForgotClose(): void {
    setForgotSentTo(null)
    setDevResetUrl(null)
  }

  if (user) {
    const to = (location.state as { from?: string } | null)?.from ?? homePathForRole(user.role)
    return <Navigate to={to} replace />
  }

  async function onSubmit(values: FormValues): Promise<void> {
    setSubmitting(true)
    try {
      const signedIn = await login(values.email, values.password)
      const from = (location.state as { from?: string } | null)?.from
      const to = from && canAccessPath(signedIn.role, from) ? from : homePathForRole(signedIn.role)
      navigate(to, { replace: true })
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        toast.error('Sign in failed', 'Check your email and password.')
      } else if (err instanceof ApiError && err.isNetwork) {
        toast.error("Couldn't reach the server", 'Try again in a moment.')
      } else {
        toast.error('Sign in failed', err instanceof Error ? err.message : 'Try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }


  return (
    <div className="login-page">
      {/* ---- Hero (navy, animated) --------------------------------------- */}
      <section className="login-hero" aria-hidden="true">
        <svg className="login-hero-ecg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0 60h300l18-40 22 80 20-60 14 20h120l18-34 22 64 18-30h140l18-40 22 80 20-60 14 20h120l18-34 22 64 18-30H1200" />
        </svg>

        <header className="login-hero-head">
          <Brand variant="light" />
        </header>

        <div className="login-hero-stage">
          <ul className="login-vitals">
            {VITALS.map((v, i) => (
              <li key={v.key} className={clsx('login-vital', revealed.has(`pill-${i}`) && 'is-in')}>
                <span className={`login-vital-icon login-vital-icon-${v.tone}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {VITAL_ICONS[v.key]}
                  </svg>
                </span>
                <span className="login-vital-body">
                  <span className="login-vital-label">{v.label}</span>
                  <span className="login-vital-value">
                    {v.value} <span className="login-vital-unit">{v.unit}</span>
                  </span>
                </span>
              </li>
            ))}
          </ul>

          <span className={clsx('login-connector', revealed.has('fax') && 'is-active')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9V4h12v5M6 18h12v3H6zM4 9h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z" />
            </svg>
          </span>

          <div className={clsx('login-fax', revealed.has('fax') && 'is-in')}>
            <div className="login-fax-head">
              <span>FAX · eCareLTC VITAL QUEUE</span>
              <span>08:04</span>
            </div>
            <div className="login-fax-rule" />
            <p className="login-fax-to">
              To <strong>Dr. Karen Whitfield</strong> · Cascade Family Practice
            </p>
            <p className="login-fax-meta">Eleanor Maddox · Room 3 · MRN-100482 · today</p>
            <dl className="login-fax-rows">
              {FAX_ROWS.map((r, i) => (
                <div key={r.label} className={clsx('login-fax-row', revealed.has(`row-${i}`) && 'is-in')}>
                  <dt>{r.label}</dt>
                  <dd className={r.high ? 'is-high' : undefined}>{r.value}</dd>
                </div>
              ))}
            </dl>
            <span className={clsx('login-fax-badge', revealed.has('badge') && 'is-in')}>
              ✓ Delivered · 08:04
            </span>
          </div>
        </div>

        <p className="login-hero-quote">"Every reading, delivered to the doctor."</p>
      </section>

      {/* ---- Form (white) ------------------------------------------------ */}
      <main className="login-form-side">
        <div className="login-form-wrap">
          <Brand variant="dark" className="login-form-brand" />

          <h1 className="login-title">Sign in</h1>
          <p className="login-sub">Welcome back. Sign in to your workspace.</p>

          <form
            className="login-form"
            onSubmit={(e: React.FormEvent) => {
              void handleSubmit(onSubmit)(e)
            }}
            noValidate
          >
            <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="you@facility.com"
                invalid={!!errors.email}
                {...register('email')}
              />
            </FormField>

            <div className="form-field">
              <div className="login-pw-labelrow">
                <label htmlFor="password" className="form-field-label">
                  Password
                  <span aria-hidden="true" className="form-field-req"> *</span>
                </label>
                <button
                  type="button"
                  className="login-forgot"
                  onClick={(e) => { void onForgot(e) }}
                  disabled={forgotSending}
                >
                  Forgot?
                </button>
              </div>
              {forgotEmailError ? (
                <p className="login-forgot-error" role="alert">{forgotEmailError}</p>
              ) : null}
              <div className="pw-wrap">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  invalid={!!errors.password}
                  {...register('password')}
                />
                <button type="button" className="pw-eye" onClick={() => setShowPw(v => !v)} tabIndex={-1} aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {errors.password?.message ? (
                <p className="form-field-error" role="alert">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            <Button type="submit" size="lg" loading={submitting} className="login-submit">
              Sign in
            </Button>
          </form>

          <p className="login-foot">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M14.8 9.3a3.5 3.5 0 1 0 0 5.4" />
            </svg>
            <span>2026 eCareLTC</span>
          </p>
        </div>
      </main>

      {/* ── Forgot-password sending spinner ─────────────────────────────── */}
      {forgotSending ? (
        <div className="login-modal-backdrop" role="status" aria-label="Sending reset link">
          <div className="login-modal login-modal--compact">
            <div className="login-modal-spinner" aria-hidden="true">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="14" stroke="#e2e8f0" strokeWidth="3.5" />
                <path d="M18 4a14 14 0 0 1 14 14" stroke="#1a56db" strokeWidth="3.5" strokeLinecap="round" />
              </svg>
            </div>
            <p className="login-modal-sending-text">Sending reset link…</p>
          </div>
        </div>
      ) : null}

      {/* ── Forgot-password confirmation modal ──────────────────────────── */}
      {forgotSentTo ? (
        <div className="login-modal-backdrop" onClick={onForgotClose} role="dialog" aria-modal="true" aria-label="Password reset sent">
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="login-modal-close" onClick={onForgotClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>

            <div className="login-modal-icon" aria-hidden="true">
              <MailIcon />
            </div>
            <h2 className="login-modal-title">Check your inbox</h2>
            <p className="login-modal-body">
              We sent a password reset link to<br />
              <strong>{forgotSentTo}</strong>
            </p>
            <p className="login-modal-hint">
              Didn't get it? Check your spam folder, or{' '}
              <button type="button" className="login-forgot-resend" onClick={onForgotClose}>
                try again
              </button>.
            </p>

            {devResetUrl ? (
              <div className="login-modal-dev">
                <span className="login-modal-dev-label">Dev — SES disabled. Use this link:</span>
                <a href={devResetUrl} className="login-modal-dev-link" target="_blank" rel="noreferrer">
                  {devResetUrl}
                </a>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function MailIcon(): React.JSX.Element {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}
function EyeIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  )
}
function EyeOffIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}
